<template>
  <div
    class="period-range-picker"
    :data-testid="dataTestidPrefix"
  >
    <p v-if="label" class="period-range-picker__label">
      {{ label }}
    </p>

    <v-menu
      v-model="isOpen"
      :close-on-content-click="false"
      offset-y
      :disabled="disabled"
      max-width="560"
      content-class="period-range-picker__menu"
      :data-testid="`${dataTestidPrefix}__menu`"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="period-range-picker__activator"
          :class="{
            'period-range-picker__activator--open': isOpen,
            'period-range-picker__activator--error': !!error,
            'period-range-picker__activator--disabled': disabled,
          }"
          v-bind="attrs"
          v-on="on"
          :disabled="disabled"
          :data-testid="`${dataTestidPrefix}__activator`"
        >
          <img src="/img/icons/fbc-icons/CalendarFilter.svg" alt="Calendar" class="period-range-picker__activator-icon">
          <span
            class="period-range-picker__activator-value"
            :class="{ 'period-range-picker__activator-value--placeholder': !displayText }"
          >
            {{ displayText || placeholder }}
          </span>
          <v-icon size="18" class="period-range-picker__activator-chevron">
            mdi-chevron-down
          </v-icon>
        </button>
      </template>

      <div
        class="period-range-picker__panel"
        :data-testid="`${dataTestidPrefix}__panel`"
      >
        <div class="period-range-picker__nav">
          <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <button
                v-bind="attrs"
                v-on="on"
                type="button"
                class="period-range-picker__nav-btn"
                aria-label="Previous month"
                :data-testid="`${dataTestidPrefix}__prev`"
                @click.stop="shiftMonths(-1)"
              >
                <v-icon size="18">mdi-chevron-left</v-icon>
              </button>
            </template>
            <span>Previous month</span>
          </v-tooltip>
          <span class="period-range-picker__nav-label">
            {{ leftMonthTitle }} – {{ rightMonthTitle }}
          </span>
          <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <button
                v-bind="attrs"
                v-on="on"
                type="button"
                class="period-range-picker__nav-btn"
                aria-label="Next month"
                :data-testid="`${dataTestidPrefix}__next`"
                @click.stop="shiftMonths(1)"
              >
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </template>
            <span>Next month</span>
          </v-tooltip>
        </div>

        <div
          class="period-range-picker__calendars"
          :class="{ 'period-range-picker__calendars--range-complete': canApply }"
        >
          <v-date-picker
            v-model="draftRange"
            range
            no-title
            full-width
            color="primary"
            class="period-range-picker__calendar"
            :picker-date="leftPickerDate"
            :data-testid="`${dataTestidPrefix}__calendar-left`"
            @update:picker-date="onLeftPickerDate"
            @click:date="onDateClick"
          />
          <v-date-picker
            v-model="draftRange"
            range
            no-title
            full-width
            color="primary"
            class="period-range-picker__calendar"
            :picker-date="rightPickerDate"
            :data-testid="`${dataTestidPrefix}__calendar-right`"
            @update:picker-date="onRightPickerDate"
            @click:date="onDateClick"
          />
        </div>

        <div class="period-range-picker__actions">
          <v-btn
            class="period-range-picker__cancel-btn text-none secondaryBtn"
            :data-testid="`${dataTestidPrefix}__cancel-btn`"
            @click.stop="handleCancel"
          >
            Cancel
          </v-btn>
          <v-btn
            small
            color="primary"
            class="period-range-picker__done-btn text-none white--text"
            :disabled="!canApply"
            :data-testid="`${dataTestidPrefix}__done-btn`"
            @click.stop="handleDone"
          >
            Done
          </v-btn>
        </div>
      </div>
    </v-menu>

    <div
      v-if="error"
      class="v-messages error--text mt-1"
      role="alert"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
/**
 * PeriodRangePicker
 *
 * Dual-month calendar for selecting a from/to period.
 * Emits `{ from, to }` on Done (and a partial `{ from }` after the first click).
 *
 * @example
 * <PeriodRangePicker :from="from" :to="to" @input="onPeriod" />
 */
import './PeriodRangePicker.scss';
import { formatPeriodRange, toDate } from '../../utils/periodHelpers';

function toIsoDate(value) {
  const d = toDate(value);
  if (!d) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addMonths(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthTitle(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
  const [y, m] = ym.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function normalizeRange(range) {
  if (!Array.isArray(range) || !range.length) return [];
  if (range.length === 1) return [range[0]];
  const [a, b] = range;
  if (!a || !b) return range.filter(Boolean);
  return a <= b ? [a, b] : [b, a];
}

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default {
  name: 'PeriodRangePicker',

  props: {
    from: {
      type: [String, Date],
      default: undefined,
    },
    to: {
      type: [String, Date],
      default: undefined,
    },
    label: {
      type: String,
      default: 'Period*',
    },
    placeholder: {
      type: String,
      default: 'Select period',
    },
    error: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dataTestidPrefix: {
      type: String,
      default: 'period-range-picker',
    },
  },

  data() {
    const left = currentYearMonth();
    return {
      isOpen: false,
      draftRange: [],
      // Independent month views — changing one calendar must not move the other
      leftPickerDate: left,
      rightPickerDate: addMonths(left, 1),
      appliedOnClose: false,
      // Prevent Vuetify range sync from overwriting independent months
      lockPickerDates: false,
    };
  },

  computed: {
    displayText() {
      return formatPeriodRange(this.from, this.to);
    },

    leftMonthTitle() {
      return monthTitle(this.leftPickerDate);
    },

    rightMonthTitle() {
      return monthTitle(this.rightPickerDate);
    },

    canApply() {
      return Array.isArray(this.draftRange) && this.draftRange.length === 2;
    },
  },

  watch: {
    isOpen(open) {
      if (open) {
        this.syncDraftFromProps();
        this.appliedOnClose = false;
      } else if (!this.appliedOnClose) {
        this.syncDraftFromProps();
      }
    },

    from() {
      if (!this.isOpen) this.syncDraftFromProps();
    },

    to() {
      if (!this.isOpen) this.syncDraftFromProps();
    },

    // Shared v-model makes each picker jump to the selected date's month.
    // Restore the months the user was viewing after selection.
    draftRange() {
      if (this.lockPickerDates) return;
      const left = this.leftPickerDate;
      const right = this.rightPickerDate;
      this.$nextTick(() => {
        if (this.leftPickerDate !== left) this.leftPickerDate = left;
        if (this.rightPickerDate !== right) this.rightPickerDate = right;
      });
    },
  },

  created() {
    this.syncDraftFromProps();
  },

  methods: {
    syncDraftFromProps() {
      const start = toIsoDate(this.from);
      const end = toIsoDate(this.to);
      this.lockPickerDates = true;

      if (start && end) {
        this.draftRange = [start, end];
        const startYm = start.slice(0, 7);
        const endYm = end.slice(0, 7);
        this.leftPickerDate = startYm;
        // Keep dual view: same month → show next month on the right
        this.rightPickerDate = endYm === startYm ? addMonths(startYm, 1) : endYm;
      } else if (start) {
        this.draftRange = [start];
        const startYm = start.slice(0, 7);
        this.leftPickerDate = startYm;
        this.rightPickerDate = addMonths(startYm, 1);
      } else {
        this.draftRange = [];
        const left = currentYearMonth();
        this.leftPickerDate = left;
        this.rightPickerDate = addMonths(left, 1);
      }

      this.$nextTick(() => {
        this.lockPickerDates = false;
      });
    },

    shiftMonths(delta) {
      this.leftPickerDate = addMonths(this.leftPickerDate, delta);
      this.rightPickerDate = addMonths(this.rightPickerDate, delta);
    },

    onLeftPickerDate(val) {
      if (this.lockPickerDates || !val) return;
      this.leftPickerDate = val.slice(0, 7);
    },

    onRightPickerDate(val) {
      if (this.lockPickerDates || !val) return;
      this.rightPickerDate = val.slice(0, 7);
    },

    onDateClick() {
      const left = this.leftPickerDate;
      const right = this.rightPickerDate;
      this.lockPickerDates = true;
      this.draftRange = normalizeRange(this.draftRange);

      // Persist start date as soon as it is chosen so E2E can reopen the panel
      // between selectPeriodFrom and selectPeriodTo without losing the start.
      if (this.draftRange.length === 1) {
        this.$emit('input', { from: this.draftRange[0], to: this.to || undefined });
      }

      this.$nextTick(() => {
        this.leftPickerDate = left;
        this.rightPickerDate = right;
        this.lockPickerDates = false;
      });
    },

    handleCancel() {
      this.appliedOnClose = true;
      this.syncDraftFromProps();
      this.isOpen = false;
    },

    handleDone() {
      if (!this.canApply) return;
      const [start, end] = normalizeRange(this.draftRange);
      this.appliedOnClose = true;
      this.$emit('input', { from: start, to: end });
      this.isOpen = false;
    },
  },
};
</script>
